import React, { useEffect, useState } from 'react';
import { Alert, Badge, Card, Col, Row, Spinner, Table } from 'react-bootstrap';
import { useAppSelector } from '../store/hooks';
import { practiceApiService, PracticeStatsDto, LeaderboardEntryDto, StreakLeaderboardEntryDto } from '../services/practiceApiService';
import { formatDuration } from '../utils/formatDuration';

const PracticeStats: React.FC = () => {
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const [stats, setStats] = useState<PracticeStatsDto | null>(null);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntryDto[]>([]);
  const [streakLeaderboard, setStreakLeaderboard] = useState<StreakLeaderboardEntryDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      isAuthenticated ? practiceApiService.getStats() : Promise.resolve(null),
      practiceApiService.getLeaderboard(),
      practiceApiService.getStreakLeaderboard(),
    ])
      .then(([statsResult, leaderboardResult, streakLeaderboardResult]) => {
        setStats(statsResult);
        setLeaderboard(leaderboardResult);
        setStreakLeaderboard(streakLeaderboardResult);
      })
      .catch(() => setError('Failed to load practice stats.'))
      .finally(() => setLoading(false));
  }, [isAuthenticated]);

  if (loading) {
    return (
      <div className="d-flex align-items-center gap-2">
        <Spinner animation="border" size="sm" />
        <span>Loading stats…</span>
      </div>
    );
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  return (
    <div>
      {isAuthenticated ? (
        <Row className="g-3 mb-4">
          <Col xs={6} md={3}>
            <Card body className="text-center h-100">
              <div className="text-muted small">Today</div>
              <div className="fs-5">{formatDuration(stats?.todaySeconds ?? 0)}</div>
            </Card>
          </Col>
          <Col xs={6} md={3}>
            <Card body className="text-center h-100">
              <div className="text-muted small">This week</div>
              <div className="fs-5">{formatDuration(stats?.weekSeconds ?? 0)}</div>
            </Card>
          </Col>
          <Col xs={6} md={3}>
            <Card body className="text-center h-100">
              <div className="text-muted small">This month</div>
              <div className="fs-5">{formatDuration(stats?.monthSeconds ?? 0)}</div>
            </Card>
          </Col>
          <Col xs={6} md={3}>
            <Card body className="text-center h-100">
              <div className="text-muted small">Streak</div>
              <div className="fs-5">
                <Badge bg={stats && stats.currentStreakDays > 0 ? 'success' : 'secondary'}>
                  {stats?.currentStreakDays ?? 0} day{stats?.currentStreakDays === 1 ? '' : 's'}
                </Badge>
              </div>
            </Card>
          </Col>
        </Row>
      ) : (
        <Alert variant="info" className="mb-4">Sign in to track and see your own practice stats.</Alert>
      )}

      <Row className="g-3">
        <Col md={6}>
          <h6>Weekly leaderboard</h6>
          {leaderboard.length === 0 ? (
            <div className="text-muted small">No practice logged this week yet.</div>
          ) : (
            <Table size="sm" striped>
              <thead>
                <tr>
                  <th>#</th>
                  <th>User</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {leaderboard.map((entry) => (
                  <tr key={entry.rank}>
                    <td>{entry.rank}</td>
                    <td>{entry.username}</td>
                    <td>{formatDuration(entry.totalSeconds)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Col>
        <Col md={6}>
          <h6>Streak leaderboard</h6>
          {streakLeaderboard.length === 0 ? (
            <div className="text-muted small">No active streaks yet.</div>
          ) : (
            <Table size="sm" striped>
              <thead>
                <tr>
                  <th>#</th>
                  <th>User</th>
                  <th>Streak</th>
                </tr>
              </thead>
              <tbody>
                {streakLeaderboard.map((entry) => (
                  <tr key={entry.rank}>
                    <td>{entry.rank}</td>
                    <td>{entry.username}</td>
                    <td>{entry.currentStreakDays} day{entry.currentStreakDays === 1 ? '' : 's'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default PracticeStats;
